import React, { useEffect, useState, useRef } from "react";
import { postShareWithEmail, getBuilder } from "../utils/bedrockAPI";
import { Modal, FormGroup, Checkbox, Button, FormControl } from "react-bootstrap";
import { View, TextInput } from "react-native";
import { getUser } from "../utils/Common";
import "./SharingModal.css";

export default function SharingModal({show,cap,setShowError,setErrorMessage,setErrorStatus,setShowSharingWindow,...props}) {
    const [emailInput, setEmailInput] = useState("");
    const [emailList, setEmailList] = useState([]);
    const [sharedType,setSharedType] = useState("DEMO");
    const [emailFocused, setEmailFocused] = useState(false);
    const [motionCheckBox, setMotionCheckBox] = useState(false);
    const [editingCheckBox, setEditingCheckBox] = useState(false);
    const [powerCheckBox, setPowerCheckBox] = useState(false);
    const [builderOrgCheckBox, setBuilderOrgCheckBox] = useState(getUser().builderId);
    const [salesDemoCheckBox, setSalesDemoCheckBox] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sharableCap, setSharableCap] = useState(cap);
    const [buttonSize, setButtonSize] = useState("large");
    const [inputError, setInputError] = useState(null);
    const isAdmin = getUser().role === 'ADMIN';
    const componentMounted = useRef(true);
    const [activeHelp, setActiveHelp] = useState(null);
    const [builder, setBuilder] = useState({name: "Organization"});

    useEffect(() => {
      if(window.innerWidth<767) {
        setButtonSize("small");
      }
      return () => { componentMounted.current = false; };
    },[]);

    useEffect(() => {
      setSharableCap(cap);
    },[cap]);

    useEffect(() => {
      if(sharableCap && (sharableCap.builderid || sharableCap.builderId)) {
        getBuilder(sharableCap.builderid || sharableCap.builderId).then(b => setBuilder(b));
      }
    },[sharableCap]);

    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleEmailInputChange = (e) => {
      setEmailInput(e.target.value);
      setInputError(null);
    };

    const handleEmailInputKeyPress = (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addEmail();
      }
    };

    const addEmail = () => {
      const email = emailInput.trim().toLowerCase();
      if (email && isValidEmail(email) && !emailList.includes(email)) {
        setEmailList([...emailList, email]);
        setEmailInput('');
        setInputError(null);
      } else if (emailList.includes(email)) {
        setInputError('Email already added');
      } else if (email && !isValidEmail(email)) {
        setInputError('Please enter a valid email address');
      }
    };

    const removeEmail = (emailToRemove) => {
      setEmailList(emailList.filter(email => email !== emailToRemove));
    };

    async function share() {
      if(emailList.length === 0) {
        setInputError("Please add at least one email address");
        return;
      }

      if(componentMounted.current) {
        setIsLoading(true);
      }

      const results = {
        success: [],
        failed: []
      };

      for (const email of emailList) {
        try {
          let c = JSON.parse(JSON.stringify(sharableCap));
          if(c.capid) {
            c.capId = c.capid;
          }
          let builderOrg = Boolean(builderOrgCheckBox);
          await postShareWithEmail(c.capId, email, sharedType, powerCheckBox, builderOrg);
          results.success.push(email);
        } catch (e) {
          console.error(`Error sharing with ${email}:`, e);
          results.failed.push(email);
        }
      }

      if (results.success.length > 0) {
        setErrorStatus("success-message");
        setErrorMessage(`Successfully shared CAP with ${results.success.length} user${results.success.length > 1 ? 's' : ''}`);
        setShowError(true);
      }
      if (results.failed.length > 0) {
        setErrorStatus("error-message");
        setErrorMessage(`Failed to share with ${results.failed.length} user${results.failed.length > 1 ? 's' : ''}`);
        setShowError(true);
      }

      setShowSharingWindow(false);
      if(componentMounted.current) {
        setIsLoading(false);
      }
    }

    const shareTypes = [{value:"DEMO",label:"Allow camera live view ONLY"}]
    shareTypes.push({value:"VIEWER",label:"Allow viewing of files, motion events, and live view"});
    shareTypes.push({value:"OWNER",label:"Allow sharing, deleting, and viewing of files, events, and live view"});
    shareTypes.push({value:"SALES_DEMO",label:"Allow sales demo access (live view and motion history but no sharing)"});

    const helpContent = {
      email: "Please enter the email address or addresses of the user(s) with whom you're sharing this camera.\n\nThis will allow them to view the camera's live feeds.\n\nIf the user(s) do not already have an account, they will receive an email to create one.",
      motion: "This will allow the user(s) to view and search through the camera's motion history",
      editing: "This will allow the user(s) to share this camera with others.\n\nUsers with sharing permissions will always have motion history permissions",
      power: "This will subscribe the user to power notification emails.",
      builderOrg: "This will add the user to your organization. You'll be able to control their access to your cameras from the Builder Access Control page.",
      salesDemo: "This will give the user access to a sales demo (live view and motion history but no sharing) and will hide the user from the access control page."
    };

    const toggleHelp = (helpType) => {
      setActiveHelp(activeHelp === helpType ? null : helpType);
    };

    const handleSharingCheckboxes = (event) => {
      if(event.target.name==="salesDemo") {
        if(salesDemoCheckBox) {
          // If unchecking sales demo, restore previous state
          setSalesDemoCheckBox(false);
          setBuilderOrgCheckBox(getUser().role!=="ADMIN");
          setSharedType("DEMO");
        } else {
          // If checking sales demo, uncheck everything else
          setSalesDemoCheckBox(true);
          setMotionCheckBox(false);
          setEditingCheckBox(false);
          setPowerCheckBox(false);
          setBuilderOrgCheckBox(false);
          setSharedType("SALES_DEMO");
        }
      } else if(event.target.name==="motion") {
        if(motionCheckBox) {
          setSharedType("DEMO");
        } else {
          setSharedType("VIEWER");
        }
        setMotionCheckBox(!motionCheckBox);
        if(editingCheckBox) {
          setEditingCheckBox(false);
        }
        setSalesDemoCheckBox(false);
      } else if (event.target.name==="editing") {
        if(editingCheckBox) {
          setSharedType("VIEWER");
        } else {
          setSharedType("OWNER");
        }
        setEditingCheckBox(!editingCheckBox);
        if(!motionCheckBox) {
          setMotionCheckBox(true);
        }
        setSalesDemoCheckBox(false);
      } else if (event.target.name==="power") {
        setPowerCheckBox(!powerCheckBox);
        setSalesDemoCheckBox(false);
      } else if (event.target.name==="builderOrg") {
        setBuilderOrgCheckBox(!builderOrgCheckBox);
        setSalesDemoCheckBox(false);
      }
    }

    return (
      <Modal
        show={show}
        onHide={() => setShowSharingWindow(false)}
        container={this}
        aria-labelledby="contained-modal-title"
        className="sharing-modal"
        dialogClassName={buttonSize==="small"?"modal-dialog-phone":"modal-dialog"}
        {...props}
      >
        <Modal.Header closeButton>
          <div className="sharing-modal-title">
            {sharableCap == null ? "Loading..." : sharableCap.name + (sharableCap.neighborhood ? ", "+sharableCap.neighborhood : "")}
          </div>
        </Modal.Header>
        <Modal.Body>
          <View style={{flex: 1, flexDirection: 'column', justifyContent: 'space-around'}}>
            <View style={{flex:1, flexDirection:'column', justifyContent: 'space-around'}}>
              <div className="intro-text">
                To share this camera with a new or existing user, fill out the following information:
              </div>
              <div className="email-section-header">
                <h2 className="email-section-title">User's Email(s)</h2>
                  <Button
                    className="sharing-help-btn"
                    onClick={() => toggleHelp('email')}
                  >
                    <i className="fas fa-question-circle"></i>
                  </Button>
              </div>
              {activeHelp === 'email' && (
                <div className="sharing-help-content">
                  {helpContent.email}
                </div>
              )}
              <div className="email-input-section">
                <div className="email-input-container">
                  <FormControl
                    type="text"
                    placeholder="Enter email addresses (press Enter or comma to add)"
                    value={emailInput}
                    onChange={handleEmailInputChange}
                    onKeyDown={handleEmailInputKeyPress}
                    className={`add-user-input ${inputError ? 'has-error' : ''}`}
                    disabled={isLoading}
                  />
                  <Button
                    onClick={addEmail}
                    disabled={!isValidEmail(emailInput) || isLoading}
                    className="add-email-button btn-brand-dark btn-compact"
                  >
                    Add
                  </Button>
                </div>
                {emailInput && !isValidEmail(emailInput) && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    Please enter a valid email address
                  </div>
                )}
                {inputError && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    {inputError}
                  </div>
                )}
                {emailList.length > 0 && (
                  <div className="email-list">
                    {emailList.map(email => (
                      <div key={email} className="email-tag">
                        <span>{email}</span>
                        <button 
                          type="button" 
                          onClick={() => removeEmail(email)}
                          disabled={isLoading}
                          className="remove-email-button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="intro-text">
                All shared user(s) will have access to this camera's live feeds. Additional permissions can be granted below.
              </div>
            </View>
            <View sytle={{paddingTop:'20px', flex:1, flexDirection:'column'}}>
              <h2 className="sharing-options-title">Additional Permissions</h2>
              <div>
                <form>
                  <FormGroup controlId="share-select">
                    <div className="checkbox-with-help">
                      <Checkbox name="motion" checked={motionCheckBox} onChange={handleSharingCheckboxes} disabled={salesDemoCheckBox}>
                        &nbsp;&nbsp;Can Access Motion History
                      </Checkbox>
                      <Button
                        className="sharing-help-btn"
                        onClick={() => toggleHelp('motion')}
                        disabled={salesDemoCheckBox}
                      >
                        <i className="fas fa-question-circle"></i>
                      </Button>
                    </div>
                    {activeHelp === 'motion' && (
                      <div className="sharing-help-content">
                        {helpContent.motion}
                      </div>
                    )}

                    <div className="checkbox-with-help">
                      <Checkbox name="editing" checked={editingCheckBox} onChange={handleSharingCheckboxes} disabled={salesDemoCheckBox || !motionCheckBox}>
                        &nbsp;&nbsp;Can Share this Camera with Others
                      </Checkbox>
                      <Button
                        className="sharing-help-btn"
                        onClick={() => toggleHelp('editing')}
                        disabled={salesDemoCheckBox || !motionCheckBox}
                      >
                        <i className="fas fa-question-circle"></i>
                      </Button>
                    </div>
                    {activeHelp === 'editing' && (
                      <div className="sharing-help-content">
                        {helpContent.editing}
                      </div>
                    )}
                  </FormGroup>
                  <h2 className="sharing-options-title">User Settings</h2>
                  <FormGroup controlId="share-select">
                    <div className="checkbox-with-help">
                      <Checkbox name="power" checked={powerCheckBox} onChange={handleSharingCheckboxes} disabled={salesDemoCheckBox}>
                        &nbsp;&nbsp;Turn on Power Notifications
                      </Checkbox>
                      <Button
                        className="sharing-help-btn"
                        onClick={() => toggleHelp('power')}
                        disabled={salesDemoCheckBox}
                      >
                        <i className="fas fa-question-circle"></i>
                      </Button>
                    </div>
                    {activeHelp === 'power' && (
                      <div className="sharing-help-content">
                        {helpContent.power}
                      </div>
                    )}

                    <div className="checkbox-with-help">
                      <Checkbox name="builderOrg" checked={builderOrgCheckBox} onChange={handleSharingCheckboxes} disabled={salesDemoCheckBox}>
                        &nbsp;&nbsp;Mark as Employee of {builder.name}
                      </Checkbox>
                      <Button
                        className="sharing-help-btn"
                        onClick={() => toggleHelp('builderOrg')}
                        disabled={salesDemoCheckBox}
                      >
                        <i className="fas fa-question-circle"></i>
                      </Button>
                    </div>
                    {activeHelp === 'builderOrg' && (
                      <div className="sharing-help-content">
                        {helpContent.builderOrg}
                      </div>
                    )}

                    {isAdmin && (
                      <div style={{marginTop: "50px"}}>
                        <div className="checkbox-with-help">
                          <Checkbox 
                            name="salesDemo" 
                            checked={salesDemoCheckBox} 
                            onChange={handleSharingCheckboxes}
                          >
                            &nbsp;&nbsp;Sales Demo (BEDROCK)
                          </Checkbox>
                          <Button
                            className="sharing-help-btn"
                            onClick={() => toggleHelp('salesDemo')}
                          >
                            <i className="fas fa-question-circle"></i>
                          </Button>
                        </div>
                        {activeHelp === 'salesDemo' && (
                          <div className="sharing-help-content">
                            {helpContent.salesDemo}
                          </div>
                        )}
                      </div>
                    )}
                  </FormGroup>
                </form>
              </div>
            </View>
            <View style={{flex: 1, flexDirection: 'row', justifyContent:"space-between", paddingTop: '20px'}}>
              <div style={{fontSize:buttonSize==="small"?"12px":"16px"}}>
                Contact <a href="mailto: help@bedrockwireless.com">help@bedrockwireless.com</a> for help with sharing
              </div>
              <Button
                className="btn-brand-dark"
                bsSize="large"
                disabled={emailList.length === 0 || isLoading}
                onClick={share}
              >
                {isLoading ? 'Sharing...' : 'Share'}
              </Button>
            </View>
          </View>
        </Modal.Body>
        {props.children}
      </Modal>
    );
  }